import type {
  AggregationsAggregate,
  AggregationsAggregationContainer,
  QueryDslQueryContainer,
  Sort,
} from '@elastic/elasticsearch/lib/api/types'
import {
  NonArray,
  SearchHitWithRequiredFields,
  SearchObjectSuperFragment,
  SearchObjectSuperGithubFragment,
  SearchObjectSuperJiraFragment,
  SearchObjectSuperLinearFragment,
  SearchObjectSuperNotionFragment,
  SearchObjectSuperSliteFragment,
} from './mapping'
import { SearchParentsHit } from './search'
import {
  GenericMapping,
  SearchResponse,
  TypedGenericMetadata,
} from './search/types'

export type Prefixed<T, P extends string | undefined = undefined> = {
  [K in Extract<keyof T, string> as P extends string ? `${P}.${K}` : K]: T[K]
}

export type SuperMedia = {
  url: string
  caption?: string
  sourceId?: string
}

export enum SuperSource {
  GOOGLE_DRIVE = 'Google Drive',
  MICROSOFT_SHAREPOINT = 'Microsoft SharePoint',
  SLACK_CHANNEL = 'Slack Channel',
  LINEAR = 'Linear',
  NOTION = 'Notion',
  WEBSITE = 'Website',
  SLITE = 'Slite',
  FILE = 'File',
  CONFLUENCE = 'Confluence',
  GITHUB = 'GitHub',
  GITLAB = 'GitLab',
  JIRA = 'Jira',
  GIT = 'Git',
  CUSTOM = 'Custom',
  HUBSPOT = 'Hubspot',
  ATTIO = 'Attio',
  ASANA = 'Asana',
  INTERCOM = 'Intercom',
}

export enum SuperSourceType {
  JIRA_ISSUE = 'jira-issue',
  GITHUB_ISSUE = 'github-issue',
  GITHUB_PR = 'github-pr',
  DRIVE_DOC = 'drive-doc',
  DOCX = 'docx',
  PDF = 'pdf',
  HTML = 'html',
  TXT = 'txt',
  PPTX = 'pptx',
  MARKDOWN = 'markdown',
  SHAREPOINT_PAGE = 'sharepoint-page',
  CONFLUENCE_PAGE = 'confluence-page',
  DRIVE_SLIDE = 'drive-slide',
  SLACK_THREAD = 'slack-thread',
  SLACK_DAILY = 'slack-daily',
  LINEAR_ISSUE = 'linear-issue',
  LINEAR_COMMENT = 'linear-comment',
  NOTION_PAGE = 'notion-page',
  NOTION_DATABASE = 'notion-database',
  NOTION_DATABASE_CHILDREN = 'notion-database-children',
  DATABASE = 'database',
  SLITE_COLLECTION = 'slite-collection',
  SLITE_COLLECTION_CHILDREN = 'slite-collection-children',
  SLITE_NOTE = 'slite-note',
  HUBSPOT_DEAL = 'hubspot-deal',
  ATTIO_NOTE = 'attio-note',
  ATTIO_ENTRY = 'attio-entry',
  ATTIO_RECORD = 'attio-record',
  ATTIO_THREAD = 'attio-thread',
  ASANA_TASK = 'asana-task',
  // This is deprecated and should not exist in the future anymore
  COLLECTION = 'collection',
  SLITE_RICH_TEXT = 'rich_text',
  SLITE_DISCUSSION = 'discussion',
  INTERCOM_CONVERSATION = 'intercom-conversation',
  GITLAB_ISSUE = 'gitlab-issue',
  GITLAB_MR = 'gitlab-merge-request',
}

export const SUPER_NON_KNOWLEDGE_SOURCES = Object.freeze([
  SuperSource.LINEAR,
  SuperSource.GITHUB,
  SuperSource.JIRA,
  SuperSource.ASANA,
  SuperSource.INTERCOM,
  SuperSource.HUBSPOT,
  SuperSource.ATTIO,
])

export const SUPER_DATABASE_RECORD_SOURCE_TYPES = Object.freeze([
  SuperSourceType.NOTION_DATABASE_CHILDREN,
  SuperSourceType.SLITE_COLLECTION_CHILDREN,
])

export type SuperNotionRequiredFields = {
  [K in keyof Pick<SearchObjectSuperNotionFragment, 'icon'>]: NonNullable<
    NonArray<SearchObjectSuperNotionFragment[K]>
  >
}

export type SuperSliteRequiredFields = {
  [K in keyof Pick<
    SearchObjectSuperSliteFragment,
    'iconColor' | 'iconShape' | 'reviewState'
  >]: NonNullable<NonArray<SearchObjectSuperSliteFragment[K]>>
}

const superSliteRequiredFieldsHelper: {
  [K in keyof Prefixed<Required<SuperSliteRequiredFields>, 'slite'>]: true
} = {
  'slite.iconColor': true,
  'slite.iconShape': true,
  'slite.reviewState': true,
}

export type SuperGithubRequiredFields = Omit<
  {
    [K in keyof SearchObjectSuperGithubFragment]: NonNullable<
      NonArray<SearchObjectSuperGithubFragment[K]>
    >
  },
  'labels' | 'assignees' | 'reviewers'
> & {
  [K in keyof Pick<
    SearchObjectSuperGithubFragment,
    'labels' | 'assignees' | 'reviewers'
  >]: NonNullable<NonArray<SearchObjectSuperGithubFragment[K]>>[]
}

// Only used to have type safety and ASK_REQUIRED_FIELDS matching AskRequiredFields type definition
const superGithubRequiredFieldsHelper: {
  [K in keyof Prefixed<Required<SuperGithubRequiredFields>, 'github'>]: true
} = {
  'github.mergedBy': true,
  'github.assignees': true,
  'github.closedAt': true,
  'github.closedBy': true,
  'github.draft': true,
  'github.labels': true,
  'github.locked': true,
  'github.mergedAt': true,
  'github.milestone': true,
  'github.number': true,
  'github.owner': true,
  'github.repo': true,
  'github.reviewers': true,
  'github.state': true,
}

export const SUPER_GITHUB_REQUIRED_FIELDS = Object.freeze(
  Object.keys(superGithubRequiredFieldsHelper)
)

export type SuperJiraRequiredFields = Omit<
  {
    [K in keyof SearchObjectSuperJiraFragment]: NonNullable<
      NonArray<SearchObjectSuperJiraFragment[K]>
    >
  },
  'labels'
> & {
  [K in keyof Pick<SearchObjectSuperJiraFragment, 'labels'>]: NonNullable<
    NonArray<SearchObjectSuperJiraFragment[K]>
  >[]
}

const superJiraRequiredFieldsHelper: {
  [K in keyof Prefixed<Required<SuperJiraRequiredFields>, 'jira'>]: true
} = {
  'jira.assignee': true,
  'jira.creator': true,
  'jira.dueDate': true,
  'jira.key': true,
  'jira.labels': true,
  'jira.percentProgress': true,
  'jira.priority': true,
  'jira.reporter': true,
  'jira.resolution': true,
  'jira.resolutionDate': true,
  'jira.status': true,
  'jira.timeSpent': true,
  'jira.type': true,
}

export const SUPER_JIRA_REQUIRED_FIELDS = Object.freeze(
  Object.keys(superJiraRequiredFieldsHelper)
)

export type SuperLinearRequiredFields = Omit<
  {
    [K in keyof SearchObjectSuperLinearFragment]: NonNullable<
      NonArray<SearchObjectSuperLinearFragment[K]>
    >
  },
  'labels'
> & {
  [K in keyof Pick<SearchObjectSuperLinearFragment, 'labels'>]: NonNullable<
    NonArray<SearchObjectSuperLinearFragment[K]>
  >[]
}

// Only used to have type safety and ASK_REQUIRED_FIELDS matching AskRequiredFields type definition
const superLinearRequiredFieldsHelper: {
  [K in keyof Prefixed<Required<SuperLinearRequiredFields>, 'linear'>]: true
} = {
  'linear.archivedAt': true,
  'linear.assignee': true,
  'linear.canceledAt': true,
  'linear.completedAt': true,
  'linear.creator': true,
  'linear.cycle': true,
  'linear.cycleNumber': true,
  'linear.estimate': true,
  'linear.identifier': true,
  'linear.labels': true,
  'linear.priority': true,
  'linear.priorityLabel': true,
  'linear.project': true,
  'linear.state': true,
  'linear.stateType': true,
  'linear.team': true,
}

export const SUPER_LINEAR_REQUIRED_FIELDS = Object.freeze(
  Object.keys(superLinearRequiredFieldsHelper)
)

export type SuperRequiredFields = Required<{
  [K in keyof Pick<
    SearchObjectSuperFragment,
    | 'semanticContent'
    | 'title'
    | 'subTitle'
    | 'id'
    | 'breadcrumb'
    | 'index'
    | 'type'
    | 'lastEditedAt'
    | 'url'
    | 'source'
    | 'rootName'
    | 'rootId'
    | 'createdAt'
  >]: NonNullable<NonArray<SearchObjectSuperFragment[K]>>
}> &
  Required<{
    [K in keyof Pick<
      SearchObjectSuperFragment,
      'parentIdList' | 'parentTitleList'
    >]: NonNullable<NonArray<SearchObjectSuperFragment[K]>>[]
  }> & {
    [K in keyof Pick<SearchObjectSuperFragment, 'media'>]: NonNullable<
      NonArray<SearchObjectSuperFragment[K]>
    >
  } & Prefixed<SuperNotionRequiredFields, 'notion'> &
  Prefixed<SuperGithubRequiredFields, 'github'> &
  Prefixed<SuperLinearRequiredFields, 'linear'> &
  Prefixed<SuperJiraRequiredFields, 'jira'> &
  Prefixed<SuperSliteRequiredFields, 'slite'>

// Only used to have type safety and ASK_REQUIRED_FIELDS matching AskRequiredFields type definition
const superRequiredFieldsHelper: {
  [K in keyof Required<SuperRequiredFields>]: true
} = {
  ...superGithubRequiredFieldsHelper,
  ...superLinearRequiredFieldsHelper,
  ...superJiraRequiredFieldsHelper,
  ...superSliteRequiredFieldsHelper,
  'notion.icon': true,
  breadcrumb: true,
  id: true,
  index: true,
  lastEditedAt: true,
  media: true,
  parentIdList: true,
  parentTitleList: true,
  semanticContent: true,
  source: true,
  subTitle: true,
  title: true,
  rootId: true,
  type: true,
  url: true,
  rootName: true,
  createdAt: true,
}

export const SUPER_REQUIRED_FIELDS = Object.freeze(
  Object.keys(superRequiredFieldsHelper)
)

export type SuperResult = SearchHitWithRequiredFields<SuperRequiredFields>

export type SuperResponse = SearchResponse<SuperResult>

export type SuperHit = SearchHitWithRequiredFields<
  SuperRequiredFields & {
    iconShape?: string
    iconColor?: string
    reviewState?: string
    markdown?: string
  }
> & { relativeId: string; mediaRelativeIds?: string[] }

export type SuperToolResultHit = {
  title: string
  dataSource: SuperSource
  source: SuperSourceType
  sourceId: string
  age?: string
  path: string
  summary?: string
  content: string
}

export type SuperSourceObject = {
  relativeId: string
  notion?: {
    icon?: string
  }
  slite?: {
    iconColor?: string
    iconShape?: string
    reviewState?: string
  }
  source: SuperSource
  type: SuperSourceType
  id: string
  index: number
  lastEditedAt: Date | string
  media: SuperMedia[]
  parents: { id: string; title: string }[]
  url: string
  explanation?: string
  title: string
}

export enum SuperTools {
  Global = 'Global',
  GitHub = SuperSource.GITHUB,
  Jira = SuperSource.JIRA,
  Linear = SuperSource.LINEAR,
  Attio = SuperSource.ATTIO,
  Asana = SuperSource.ASANA,
  Intercom = SuperSource.INTERCOM,
  Hubspot = SuperSource.HUBSPOT,
}

export type AskMedia = {
  type: string | null
  source: SuperSourceObject | undefined
  mediaUrl: string
  title: string
}

export type SuperStreamEventSources = {
  sources: SuperSourceObject[]
}

export type SuperStreamEventSourceExplanation = {
  relativeId: string
  explanation: string
}

export type SuperStreamEventToolExplanationText = {
  text: string
  tool: SuperTools
}

export enum SuperStructuredType {
  Filtered = 'Filtered',
  Buckets = 'Buckets',
  Raw = 'Raw',
  Metric = 'Metric',
}

export type FilteredGeneric<T extends readonly GenericMapping[]> = {
  type: SuperStructuredType.Filtered
  result: (TypedGenericMetadata<T> & {
    lastEditedAt: Date | string
    createdAt: Date | string
    title: string
    url: string
  })[]
}

export type FilteredLinear = {
  type: SuperStructuredType.Filtered
  result: (SuperLinearRequiredFields & {
    lastEditedAt: Date | string
    createdAt: Date | string
    title: string
    url: string
  })[]
}

export enum RawChartType {
  Line = 'line',
  Bar = 'bar',
  Pie = 'pie',
  Doughnut = 'doughnut',
  Radar = 'radar',
}

export type ChartArtifact = {
  type: RawChartType
  data: Record<string, AggregationsAggregate>
  chartConverter: string
}

export type Raw = {
  type: SuperStructuredType.Raw
  result: ChartArtifact
}

export type MetricArtifact = {
  value: number
  unit: string
}

export type Metric = {
  type: SuperStructuredType.Metric
  result: MetricArtifact
}

export type SuperStreamEventLinear = { esParams?: unknown } & (
  | FilteredLinear
  | Raw
  | Metric
)

export type SuperStreamEventGeneric<
  T extends readonly GenericMapping[] = readonly GenericMapping[]
> = {
  esParams?: unknown
} & (FilteredGeneric<T> | Raw | Metric)

export type SuperStreamEventTokenConsumption = {
  input: number
  output: number
  byModel?: {
    [key: string]: {
      input: number
      output: number
    }
  }
}

export type FilteredGithubArtifact = (SuperGithubRequiredFields & {
  lastEditedAt: Date | string
  createdAt: Date | string
  title: string
  url: string
})[]

export type FilteredGithub = {
  type: SuperStructuredType.Filtered
  result: (SuperGithubRequiredFields & {
    lastEditedAt: Date | string
    createdAt: Date | string
    title: string
    url: string
  })[]
}

export type FilteredJira = {
  type: SuperStructuredType.Filtered
  result: (SuperJiraRequiredFields & {
    lastEditedAt: Date
    createdAt: Date
    title: string
    url: string
  })[]
}

export type SuperStreamEventGithub = { esParams?: unknown } & (
  | FilteredGithub
  | Raw
  | Metric
)

export type SuperStreamEventJira = { esParams?: unknown } & (
  | FilteredJira
  | Raw
  | Metric
)

export type MediaArtifact = SuperMedia

export enum SuperColumnTypes {
  DATE = 'date',
  MULTI_SELECT = 'multi-select',
  NUMBER = 'number',
  TEXT = 'text',
  LINK = 'link',
  TODO = 'todo',
}

export type SuperColumnDefinition = {
  type: SuperColumnTypes
  name: string
}

export type SuperRecord = (
  | {
      type: SuperColumnTypes.DATE
      value: string
    }
  | { type: SuperColumnTypes.LINK; url: string; title: string }
  | { type: SuperColumnTypes.MULTI_SELECT; options: string[] }
  | { type: SuperColumnTypes.NUMBER; value: number }
  | { type: SuperColumnTypes.TEXT; value: string }
  | { type: SuperColumnTypes.TODO; value: boolean }
)[]

export type TableArtifact = {
  columnDefs: SuperColumnDefinition[]
  records: SuperRecord[]
}

export type OldTableArtifact = FilteredLinear | FilteredJira | FilteredGithub

export enum ArtifactType {
  Media = 'media',
  Chart = 'chart',
  Table = 'table',
  Metric = 'metric',
  OldTable = 'old-table',
}

export type SuperStreamEventArtifact = {
  relativeId: string
  artifact:
    | { type: ArtifactType.Media; content: MediaArtifact; tool?: SuperTools }
    | { type: ArtifactType.Chart; content: ChartArtifact; tool?: SuperTools }
    | { type: ArtifactType.Table; content: TableArtifact; tool?: SuperTools }
    | {
        type: ArtifactType.OldTable
        content: OldTableArtifact
        tool?: SuperTools
      }
    | { type: ArtifactType.Metric; content: MetricArtifact; tool?: SuperTools }
}

export type SuperStreamEventArtifacts = {
  artifacts: SuperStreamEventArtifact[]
}

export type SuperStreamEventSearchFilters = {
  authors?: { id: string; email: string; displayName: string }[]
  source?: SuperSource
  parents?: SearchParentsHit[]
  range?: {
    lastEditedAt?: {
      gte?: string
      lte?: string
    }
  }
  semanticSearchQueries?: string[]
}

export type SuperStreamEventBuildingAnalysis = {
  tools?: SuperTools[]
  askPrecisions?: string
  instructions?: {
    instructions?: string
    type?: 'simple' | 'map' | 'reduce'
  }
  dateRangeFilter?: {
    gte?: string
    lte?: string
  }
  authorFilters?: string[]
  parentFilters?: string[]
  keywordsSearch?: string[]
  semanticSearch?: string[]
}

export type ESParams = {
  query?: QueryDslQueryContainer
  aggs?: Record<string, AggregationsAggregationContainer>
  aggregations?: Record<string, AggregationsAggregationContainer>
  sort?: Sort
  size?: number
}

export const SuperAnalysisKeys: {
  [K in keyof Required<SuperStreamEventBuildingAnalysis>]: K
} = {
  tools: 'tools',
  askPrecisions: 'askPrecisions',
  authorFilters: 'authorFilters',
  dateRangeFilter: 'dateRangeFilter',
  instructions: 'instructions',
  keywordsSearch: 'keywordsSearch',
  parentFilters: 'parentFilters',
  semanticSearch: 'semanticSearch',
} as const

export type SuperAnalysisKeys = keyof typeof SuperAnalysisKeys

export type SuperAuthorizationErrorPayload = {
  id?: string
  message: string
}

export type SuperAuthorizationSuccessPayload = {
  user: {
    id: string
    hasSuperSeat: boolean | null
  }
  organizationMetadata: {
    superEnabled: boolean | null
  }
  organization: {
    id: string
  }
}

export type SuperAuthorizationPayload =
  | SuperAuthorizationSuccessPayload
  | SuperAuthorizationErrorPayload

export type SuperFeatures = {
  useHistory: boolean
  citeSourceInPrompt: boolean
  useArtifactsInAnswer: boolean
  followupClarification: boolean
  explainSources: boolean
  explainCitedSources: boolean
  promptCaching: boolean
  queryPlannerOnly?: boolean
  coordinatorOnly?: boolean
}

export type SuperStreamEventFailingTool = {
  tool: SuperTools
  error?: {
    message?: string
    id?: string
  }
}

export type SuperToolResultDataSources = {
  sources: SuperToolResultHit[]
}

export enum SuperToolChainEventId {
  TEXT = 'text',
  TOOL_CALL = 'tool-call',
  TOOL_RESULT = 'tool-result',
  PLAN_RESULT = 'plan-result',
}

export type SuperToolChainEventText = {
  id: SuperToolChainEventId.TEXT
  event: {
    text: string
    source: string
  }
}

export type SuperToolChainEventToolCall = {
  id: SuperToolChainEventId.TOOL_CALL
  event: {
    tool: string
    parameters: Record<string, unknown>
    toolCallId: string
  }
}

export type SuperToolChainEventToolResult = {
  id: SuperToolChainEventId.TOOL_RESULT
  event: {
    tool: string
    toolCallId: string
    result: Record<string, unknown>
    content?: string
  }
}

export type SuperToolChainEventPlanResult = {
  id: SuperToolChainEventId.PLAN_RESULT
  event: {
    steps: {
      stepId: number
      explanation: string
      instructions?: string
      tool: string
      parameters?: Record<string, unknown>
    }[]
  }
}

export type SuperToolChainEvent =
  | SuperToolChainEventText
  | SuperToolChainEventToolCall
  | SuperToolChainEventToolResult
  | SuperToolChainEventPlanResult

export function isSupportedSuperToolChainEventId(
  askEventId: string | undefined
): askEventId is SuperToolChainEventId {
  return Boolean(
    askEventId &&
      Object.values(SuperToolChainEventId)
        .map((a) => a.toString())
        .includes(askEventId)
  )
}
